import { useState, useMemo } from 'react';
import {
  FrequencyTypeEnum,
  ListTokenFrequency,
  VenueTypeEnum,
} from '../../../../../../providers/token-volatility-timeseries-provider';
import { WindowTenorEnum } from './use-series-manager';

interface useRealizedOptionsProps {
  listTokenFrequency: ListTokenFrequency;
}

const getMainTokensForVenue = (venue: VenueTypeEnum, allTokenOptions: any[]) => {
  const priority = ['BTC', 'ETH', 'SOL', 'XRP', '1000PEPE', '1000SHIB'];

  if (venue === VenueTypeEnum.DERIBIT) {
    return ['BTC_USDC', 'ETH_USDC', 'SOL_USDC', 'XRP_USDC'];
  }

  const found = priority
    .map((sym) => allTokenOptions.find((o) => o?.label === sym)?.value)
    .filter((v): v is string => Boolean(v));

  if (found.length === 0) return allTokenOptions.slice(0, 5).map((t) => t.value);

  return found;
};

const formatTokenLabel = (token: string) => token.replace(/(_)?(USDC|USDT|USD)$/g, '');
const formatFrequencyLabel = (frequency: FrequencyTypeEnum) => {
  const value = parseInt(frequency);

  if (value < 60) {
    return `${value} min`;
  } else if (value === 60) {
    return `1h`;
  }

  return frequency;
};

export const useRealizedOptions = (props: useRealizedOptionsProps) => {
  const { listTokenFrequency } = props;

  const [venue, setVenue] = useState<VenueTypeEnum>(VenueTypeEnum.BINANCE);
  const [token, setToken] = useState<string>('');
  const [window, setWindow] = useState<WindowTenorEnum>(WindowTenorEnum.W1);
  const [frequency, setFrequency] = useState<FrequencyTypeEnum>(FrequencyTypeEnum.hour1);

  const handleVenues = (list: ListTokenFrequency) => {
    const venueOptions = list.map((v) => ({
      label: v.name,
      value: v.name as VenueTypeEnum,
    }));

    return venueOptions;
  };

  const handleTokens = (list: ListTokenFrequency, venue: VenueTypeEnum) => {
    const venueData = list.find((v) => v.name === venue);
    if (!venueData) return { mainOptions: [], otherOptions: [] };

    const allTokenOptions = venueData.tokens.map((t) => ({
      label: formatTokenLabel(t.name),
      value: t.name,
    }));

    const mainTokenValues = getMainTokensForVenue(venue, allTokenOptions);
    const mainOptions = mainTokenValues
      .map((val) => allTokenOptions.find((t) => t.value === val))
      .filter(Boolean);
    const otherOptions = allTokenOptions.filter((t) => !mainTokenValues.includes(t.value));

    if (allTokenOptions.length > 0 && !allTokenOptions.find((t) => t.value === token)) {
      const firstPreferred = mainOptions[0] ?? allTokenOptions[0];
      if (firstPreferred) setToken(firstPreferred.value);
    }

    return { mainOptions, otherOptions };
  };

  const handleWindows = (list: ListTokenFrequency, venue: VenueTypeEnum, token: string) => {
    const venueData = list.find((v) => v.name === venue);
    if (!venueData) return [];

    const tokenData = venueData.tokens.find((t) => t.name === token);
    if (!tokenData) return [];

    const windowOptions = tokenData.windows.map((w) => ({
      label: w.name,
      value: w.name as WindowTenorEnum,
    }));

    if (windowOptions.length > 0 && !windowOptions.find((w) => w.value === window)) {
      setWindow(windowOptions[0].value);
    }

    return windowOptions;
  };

  const handleFrequencies = (
    list: ListTokenFrequency,
    venue: VenueTypeEnum,
    token: string,
    window: WindowTenorEnum,
    frequency: FrequencyTypeEnum,
    setFrequency: (value: FrequencyTypeEnum) => void,
  ) => {
    const venueData = list.find((v) => v.name === venue);
    if (!venueData) return [];

    const tokenData = venueData.tokens.find((t) => t.name === token);
    if (!tokenData) return [];

    const windowData = tokenData.windows.find((w) => w.name === window);
    if (!windowData) return [];

    const frequencyOptions = windowData.frequencies.map((f) => ({
      label: formatFrequencyLabel(f as FrequencyTypeEnum),
      value: f as FrequencyTypeEnum,
    }));

    if (frequencyOptions.length > 0 && !frequencyOptions.find((f) => f.value === frequency)) {
      setFrequency(frequencyOptions[0]?.value as FrequencyTypeEnum);
    }

    return frequencyOptions;
  };

  const options = useMemo(() => {
    const venueOptions = handleVenues(listTokenFrequency);
    const { mainOptions: tokenOptions, otherOptions: otherTokenOptions } = handleTokens(
      listTokenFrequency,
      venue,
    );
    const windowOptions = handleWindows(listTokenFrequency, venue, token);
    const frequencyOptions = handleFrequencies(
      listTokenFrequency,
      venue,
      token,
      window,
      frequency,
      setFrequency,
    );

    return [
      {
        key: 'venue',
        value: venue,
        setValue: setVenue,
        label: 'Venue',
        options: venueOptions,
      },
      {
        key: 'token',
        value: token,
        setValue: setToken,
        label: 'Token',
        options: tokenOptions,
        otherOptions: otherTokenOptions,
      },
      {
        key: 'window',
        value: window,
        setValue: setWindow,
        label: 'Window',
        options: windowOptions,
      },
      {
        key: 'frequency',
        value: frequency,
        setValue: setFrequency,
        label: 'Frequency',
        options: frequencyOptions,
      },
    ];
  }, [venue, token, window, frequency, listTokenFrequency]);

  return {
    options,
  };
};
